import { ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BulkInviteEmployeesResponse, CrmService } from '../../../../../services/crm.service';
import { NotificationService } from '../../../../../services/notifications.service';
import { IGroups } from '../../../../../interfaces';
import { DEFAULT_ERROR_STRING, ERROR_MESSAGES_STRING } from '../../../../../enum/constants';

type InviteEmployeeForm = FormGroup<{
  email: FormControl<string>;
  phone: FormControl<string>;
  first_name: FormControl<string>;
  last_name: FormControl<string>;
  group_id: FormControl<string | null>;
}>;

/** Backend limit for one POST /employees/invites request */
const MAX_INVITES = 50;

// Some shared fields (e.g. app-phone-field) write null into the control when cleared
const trimValue = (value: unknown): string => String(value ?? '').trim();

const requiredTrimmed = (control: AbstractControl): ValidationErrors | null =>
  trimValue(control.value) ? null : { required: true };

@Component({
  selector: 'app-invite-employees',
  templateUrl: './invite-employees.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class InviteEmployeesComponent implements OnInit {
  @Output() close = new EventEmitter<'success' | 'close'>();

  private readonly crmService = inject(CrmService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translateService = inject(TranslateService);
  private readonly notificationService = inject(NotificationService);

  protected readonly rows = new FormArray<InviteEmployeeForm>([]);
  protected readonly isRegister = new FormControl(true, { nonNullable: true });
  protected readonly isSkipEmail = new FormControl(false, { nonNullable: true });
  protected readonly rowErrors = new Map<InviteEmployeeForm, string>();
  protected groups: IGroups[] = [];
  protected isGroupsLoading = false;
  protected isSubmitting = false;
  private hasInvited = false;
  protected readonly MAX_INVITES = MAX_INVITES;

  ngOnInit(): void {
    this.addRow();
    this.loadGroups();
  }

  protected addRow(): void {
    if (this.rows.length >= MAX_INVITES) return;

    const lastRow = this.rows.at(this.rows.length - 1);
    this.rows.push(this.createRow(lastRow?.controls.group_id.value ?? null));
  }

  protected removeRow(index: number): void {
    if (this.rows.length === 1) return;

    this.rowErrors.delete(this.rows.at(index));
    this.rows.removeAt(index);
  }

  protected isInvalid(row: InviteEmployeeForm, field: keyof InviteEmployeeForm['controls']): boolean {
    const control = row.controls[field];
    return control.touched && control.invalid;
  }

  protected hasInvalidField(row: InviteEmployeeForm): boolean {
    return Object.values(row.controls).some((control) => control.touched && control.invalid);
  }

  protected onCancel(): void {
    this.close.emit(this.hasInvited ? 'success' : 'close');
  }

  protected onInvite(): void {
    if (this.isSubmitting) return;

    this.rows.markAllAsTouched();
    this.rowErrors.clear();
    if (this.rows.invalid || this.markDuplicateEmails()) return;

    this.isSubmitting = true;
    this.crmService
      .inviteEmployees({
        managers: this.rows.controls.map((row) => {
          const { email, phone, first_name, last_name, group_id } = row.getRawValue();
          return {
            email: this.normalizeEmail(email),
            phone: trimValue(phone),
            first_name: trimValue(first_name),
            last_name: trimValue(last_name),
            group_id: group_id as string,
          };
        }),
        is_register: this.isRegister.value,
        is_skip_email: this.isSkipEmail.value,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isSubmitting = false)),
      )
      .subscribe({
        next: (res) => this.applyInviteResults(res),
        error: (e) =>
          this.notificationService.showError(
            '',
            this.translateService.instant(this.getErrorKey(e?.error?.errors?.[0]?.code)),
          ),
      });
  }

  /** The backend rejects the whole request if emails repeat, so point at the duplicates before sending */
  private markDuplicateEmails(): boolean {
    const rowsByEmail = new Map<string, InviteEmployeeForm[]>();
    for (const row of this.rows.controls) {
      const email = this.normalizeEmail(row.controls.email.value);
      rowsByEmail.set(email, [...(rowsByEmail.get(email) || []), row]);
    }

    let hasDuplicates = false;
    for (const rows of rowsByEmail.values()) {
      if (rows.length < 2) continue;

      hasDuplicates = true;
      rows.forEach((row) => this.rowErrors.set(row, 'invite_duplicate_email'));
    }
    return hasDuplicates;
  }

  private applyInviteResults(res: BulkInviteEmployeesResponse): void {
    const resultsByEmail = new Map((res?.items || []).map((item) => [this.normalizeEmail(item.email), item]));

    for (const row of [...this.rows.controls]) {
      const result = resultsByEmail.get(this.normalizeEmail(row.controls.email.value));
      if (!result) continue;

      if (result.success) {
        this.hasInvited = true;
        this.rows.removeAt(this.rows.controls.indexOf(row));
      } else {
        this.rowErrors.set(row, this.getErrorKey(result.error?.code));
      }
    }

    if (!this.rows.length) this.close.emit('success');
  }

  private normalizeEmail(email: unknown): string {
    return trimValue(email).toLowerCase();
  }

  /** Most API error codes map to `errors.<code>`, but ERROR_MESSAGES_STRING doesn't list the invite ones */
  private getErrorKey(code?: string): string {
    if (!code) return DEFAULT_ERROR_STRING;
    if (ERROR_MESSAGES_STRING[code]) return ERROR_MESSAGES_STRING[code];

    const key = `errors.${code.toLowerCase()}`;
    return this.translateService.instant(key) !== key ? key : DEFAULT_ERROR_STRING;
  }

  private createRow(groupId: string | null): InviteEmployeeForm {
    return new FormGroup({
      email: new FormControl('', { nonNullable: true, validators: [requiredTrimmed, Validators.email] }),
      phone: new FormControl('', { nonNullable: true, validators: requiredTrimmed }),
      first_name: new FormControl('', { nonNullable: true, validators: requiredTrimmed }),
      last_name: new FormControl('', { nonNullable: true, validators: requiredTrimmed }),
      group_id: new FormControl<string | null>(groupId, Validators.required),
    });
  }

  private loadGroups(): void {
    this.isGroupsLoading = true;
    this.crmService
      .getAllGroups({ page: 1, take: 25, access_change_own_role: true })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isGroupsLoading = false)),
      )
      .subscribe({
        next: (res) => (this.groups = res?.items || []),
        error: () => (this.groups = []),
      });
  }
}
